import React, { useRef, useState } from 'react';
import { getInitials } from '../lib/utils';

const truncateText = (text, maxLength = 20) => {
  if (!text) return { display: 'N/A', truncated: false };
  if (text.length <= maxLength) return { display: text, truncated: false };
  return { display: `${text.substring(0, maxLength)}...`, truncated: true };
};

const SavedAlumniTable = ({
  rows,
  totalCount,
  filteredCount,
  activeFilterCount,
  sortColumn,
  sortDirection,
  onClearFilters,
  onSort,
  filters,
  onUpdateFilters,
  pendingFilters,
  openFilterDropdown,
  setOpenFilterDropdown,
  setPendingFilters,
  getUniqueValuesWithCounts,
  getFilteredRowsExcluding,
  categories,
  showCategoryDropdown,
  onToggleCategoryDropdown,
  selectedCategoryFilter,
  onSelectCategoryFilter,
  userCategories,
  showManualEntry,
  manualEntry,
  onManualEntryChange,
  onManualEntrySubmit,
  addingManual,
  onToggleManualEntry,
  candidateSaved,
  candidateEmails,
  onOpenEmailModal,
  onOpenNotesModal,
  onRemoveAlumni,
  onFieldUpdate,
  comments,
  expandedCells,
  onToggleCellExpansion,
  updateAlumniCategory,
  deleteCategory,
  createCategory,
  showCreateCategory,
  onOpenCreateCategory,
  onCloseCreateCategory,
  onFileInputChange,
  uploadingResume,
}) => {
  const fileInputRef = useRef(null);
  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };
  
  const [editingField, setEditingField] = useState(null); // Format: "rowId_fieldName"
  const [editValue, setEditValue] = useState('');
  
  const handleDoubleClick = (rowId, fieldName, currentValue) => {
    setEditingField(`${rowId}_${fieldName}`);
    setEditValue(currentValue || '');
  };
  
  const handleBlur = (rowId, fieldName) => {
    if (editValue !== undefined) {
      onFieldUpdate(rowId, fieldName, editValue);
    }
    setEditingField(null);
    setEditValue('');
  };
  
  const handleKeyDown = (e, rowId, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editValue !== undefined) {
        onFieldUpdate(rowId, fieldName, editValue);
      }
      setEditingField(null);
      setEditValue('');
    } else if (e.key === 'Escape') {
      setEditingField(null);
      setEditValue('');
    }
  };
  const renderTruncated = (text, rowId, column, maxLength = 20) => {
    if (!text) return <span className="text-gray-400">N/A</span>;
    const { display, truncated } = truncateText(text, maxLength);
    if (!truncated) {
      return display;
    }
    const key = `${rowId}_${column}`;
    const expanded = expandedCells[key];
    if (expanded) {
      return (
        <span className="cursor-pointer hover:text-blue-400" onClick={() => onToggleCellExpansion(rowId, column)}>
          {text}
        </span>
      );
    }
    return (
      <span className="cursor-pointer hover:text-blue-400" onClick={() => onToggleCellExpansion(rowId, column)} title="Click to expand">
        {display}
      </span>
    );
  };

  const filterColumns = [
    { key: 'position', label: 'Position' },
    { key: 'company', label: 'Company' },
    { key: 'category', label: 'Category' },
  ];

  const handleFilterToggle = (column, value, checked) => {
    setPendingFilters(prev => {
      const current = prev[column] || filters[column] || [];
      const nextValues = checked ? Array.from(new Set([...current, value])) : current.filter(v => v !== value);
      const updatedFilters = { ...filters, [column]: nextValues };
      Object.keys(updatedFilters).forEach(key => {
        if (!updatedFilters[key] || updatedFilters[key].length === 0) {
          delete updatedFilters[key];
        }
      });
      onUpdateFilters(updatedFilters);
      return { ...prev, [column]: nextValues };
    });
  };

  return (
    <div className="bg-[#242831] rounded-lg shadow-lg mb-6" style={{ overflow: 'visible' }}>
      <div className="px-4 py-3 bg-[#2d3139] border-b border-[#374151] flex items-center justify-between">
        <div className="text-sm text-[#9ca3af]">
          {filteredCount !== totalCount ? (
            <>
              Showing <span className="font-semibold text-[#e4e6eb]">{filteredCount}</span> of{' '}
              <span className="font-semibold text-[#e4e6eb]">{totalCount}</span> alumni
            </>
          ) : (
            <>{totalCount} alumni</>
          )}
          {sortColumn && (
            <span className="ml-3 text-xs">• Sorted by {sortColumn} ({sortDirection === 'asc' ? 'A-Z' : 'Z-A'})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={triggerFilePicker}
            className="px-4 py-2 rounded text-sm font-semibold bg-[#10b981] text-white hover:bg-[#0ea46e] transition-colors whitespace-nowrap"
            title="Upload resume for automatic bio generation"
            disabled={uploadingResume}
          >
            {uploadingResume ? 'Uploading...' : '📎 Upload Resume'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={onFileInputChange}
          />
          {(activeFilterCount > 0 || sortColumn) && (
            <button
              onClick={onClearFilters}
              className="px-3 py-1.5 bg-[#374151] text-[#e4e6eb] rounded text-xs font-medium hover:bg-[#4b5563] transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto md:overflow-x-visible">
        <table className="w-full table-auto min-w-full">
          <thead className="bg-[#2d3139] relative">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-[#9ca3af] uppercase tracking-wider cursor-pointer hover:bg-[#374151] transition-colors relative"
                onClick={() => onSort('name')}
              >
                <div className="flex items-center gap-2">
                  <span>Name</span>
                  {sortColumn === 'name' && <span className="text-[#10b981]">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              {filterColumns.map(column => (
                <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold text-[#9ca3af] uppercase tracking-wider relative">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 cursor-pointer hover:text-[#e4e6eb] transition-colors" onClick={() => onSort(column.key)}>
                      {column.label}
                      {sortColumn === column.key && (
                        <span className="ml-1 text-[#10b981]">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </span>
                    <button
                      data-filter-button
                      onClick={event => {
                        event.stopPropagation();
                        if (openFilterDropdown === column.key) {
                          setOpenFilterDropdown(null);
                          setPendingFilters({});
                        } else {
                          const filteredRows = getFilteredRowsExcluding(rows, column.key);
                          const uniqueValues = getUniqueValuesWithCounts(filteredRows, column.key);
                          setPendingFilters({ ...filters });
                          setOpenFilterDropdown(column.key);
                          setPendingFilters(prev => ({ ...prev, __options: uniqueValues }));
                        }
                      }}
                      className="p-2 hover:bg-[#3b3f4a] rounded transition-colors text-[#9ca3af]"
                      title={`Filter by ${column.label.toLowerCase()}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                    {openFilterDropdown === column.key && (
                      <div className="absolute right-0 top-full mt-2 w-64 bg-[#23242a] border border-[#374151] rounded-lg shadow-xl z-10" data-filter-dropdown>
                        <div className="p-3 border-b border-[#374151] flex items-center justify-between">
                          <span className="text-sm font-semibold text-[#e4e6eb]">Filter by {column.label}</span>
                          <button
                            onClick={() => {
                              setOpenFilterDropdown(null);
                              setPendingFilters({});
                            }}
                            className="text-[#9ca3af] hover:text-[#e4e6eb] transition-colors"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                          {(pendingFilters.__options || []).map(({ value, count }) => {
                            if (!value) return null;
                            const currentFilterValues = pendingFilters[column.key] || [];
                            const filteredRows = getFilteredRowsExcluding(rows, column.key);
                            const disabled =
                              !filteredRows.some(row => {
                                let rowValue = '';
                                switch (column.key) {
                                  case 'position':
                                    rowValue = row.alumni_position || 'N/A';
                                    break;
                                  case 'company':
                                    rowValue = row.alumni_company || 'N/A';
                                    break;
                                  case 'category':
                                    rowValue = categories[row.id] || 'None';
                                    break;
                                  default:
                                    rowValue = '';
                                }
                                return rowValue === value;
                              }) && !currentFilterValues.includes(value);
                            return (
                              <label
                                key={value}
                                  className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-[#2f313d] ${
                                    disabled ? 'opacity-40 cursor-not-allowed' : 'text-[#e4e6eb]'
                                  }`}
                              >
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={currentFilterValues.includes(value)}
                                    disabled={disabled}
                                    onChange={e => handleFilterToggle(column.key, value, e.target.checked)}
                                    className="rounded border-[#4b5563] bg-[#1f232d] text-[#10b981] focus:ring-[#10b981]"
                                  />
                                  <span>{value}</span>
                                </div>
                                <span className="text-xs text-[#9ca3af]">{count}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="p-3 border-t border-[#374151] flex items-center gap-2">
                          <button
                            onClick={() => {
                              const updatedFilters = { ...filters };
                              delete updatedFilters[column.key];
                              onUpdateFilters(updatedFilters);
                              setPendingFilters(prev => ({ ...prev, [column.key]: [] }));
                            }}
                            className="flex-1 px-3 py-1.5 bg-[#374151] text-[#e4e6eb] rounded text-sm font-semibold hover:bg-[#4b5563] transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#9ca3af] uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#9ca3af] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2d3139] bg-[#242831]">
            {rows.map(row => (
              <tr key={row.id} className="bg-[#242831] hover:bg-[#2d3139] transition-colors">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    {row.alumni_linkedin ? (
                      <a
                        href={row.alumni_linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center"
                        title="Open LinkedIn profile"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-[#0a66c2]">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </a>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#0a66c2] flex items-center justify-center text-white font-semibold shadow-md text-xs" title="No LinkedIn URL">
                        {getInitials(row.alumni_name)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[#e4e6eb] font-medium break-words">{row.alumni_name || 'Unnamed'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-[#e4e6eb] whitespace-normal break-words">
                  {editingField === `${row.id}_alumni_position` ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => handleBlur(row.id, 'alumni_position')}
                      onKeyDown={e => handleKeyDown(e, row.id, 'alumni_position')}
                      autoFocus
                      className="w-full bg-[#2d3139] border border-[#10b981] text-[#e4e6eb] focus:outline-none focus:ring-1 focus:ring-[#10b981] rounded px-2 py-1"
                    />
                  ) : (
                    <div
                      onDoubleClick={() => handleDoubleClick(row.id, 'alumni_position', row.alumni_position)}
                      className="cursor-pointer hover:bg-[#2d3139] rounded px-1 -mx-1"
                      title="Double-click to edit"
                    >
                      {renderTruncated(row.alumni_position, row.id, 'position')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 text-[#e4e6eb] whitespace-normal break-words">
                  {editingField === `${row.id}_alumni_company` ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => handleBlur(row.id, 'alumni_company')}
                      onKeyDown={e => handleKeyDown(e, row.id, 'alumni_company')}
                      autoFocus
                      className="w-full bg-[#2d3139] border border-[#10b981] text-[#e4e6eb] focus:outline-none focus:ring-1 focus:ring-[#10b981] rounded px-2 py-1"
                    />
                  ) : (
                    <div
                      onDoubleClick={() => handleDoubleClick(row.id, 'alumni_company', row.alumni_company)}
                      className="cursor-pointer hover:bg-[#2d3139] rounded px-1 -mx-1"
                      title="Double-click to edit"
                    >
                      {renderTruncated(row.alumni_company, row.id, 'company')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <select
                      value={categories[row.id] || 'None'}
                      onChange={e => updateAlumniCategory(row.id, e.target.value === 'None' ? null : e.target.value)}
                      className="bg-[#23242a] text-white text-sm rounded px-2 py-1 border border-gray-700 focus:border-[#19C37D] focus:ring-1 focus:ring-[#19C37D]"
                    >
                      <option value="None">None</option>
                      {userCategories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={onOpenCreateCategory}
                      className="p-1 text-[#9ca3af] hover:text-[#e4e6eb] transition-colors"
                      title="Create category"
                    >
                      +
                    </button>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-[#e4e6eb] text-sm">
                    {candidateSaved[row.id] && candidateEmails[row.id] ? (
                      <span className="font-mono">{candidateEmails[row.id]}</span>
                    ) : editingField === `${row.id}_alumni_email` ? (
                      <input
                        type="email"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onBlur={() => handleBlur(row.id, 'alumni_email')}
                        onKeyDown={e => handleKeyDown(e, row.id, 'alumni_email')}
                        autoFocus
                        className="w-full bg-[#2d3139] border border-[#10b981] text-[#e4e6eb] font-mono focus:outline-none focus:ring-1 focus:ring-[#10b981] rounded px-2 py-1"
                        placeholder="email@example.com"
                      />
                    ) : (
                      <span
                        onDoubleClick={() => handleDoubleClick(row.id, 'alumni_email', row.alumni_email)}
                        className="font-mono cursor-pointer hover:bg-[#2d3139] rounded px-1 -mx-1 block"
                        title="Double-click to edit"
                      >
                        {row.alumni_email || <span className="text-gray-500">Not available</span>}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => onOpenEmailModal(row)}
                      className="px-3 py-1.5 text-xs bg-[#10b981] text-white rounded-lg hover:bg-[#059669] transition-colors"
                    >
                      ✉️ Email
                    </button>
                    <button
                      onClick={() => onOpenNotesModal(row)}
                      className="px-3 py-1.5 text-xs bg-[#374151] text-[#e4e6eb] rounded-lg hover:bg-[#4b5563] transition-colors"
                    >
                      📝 Notes
                    </button>
                    <button
                      onClick={() => onRemoveAlumni(row)}
                      className="px-3 py-1.5 text-xs bg-[#7f1d1d] text-white rounded-lg hover:bg-[#991b1b] transition-colors"
                    >
                      🗑️ Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {showManualEntry && (
              <tr className="bg-[#1f232d]">
                <td colSpan={6} className="px-4 py-4">
                  <form onSubmit={onManualEntrySubmit} className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    {[
                      { key: 'name', placeholder: 'Full Name', required: true },
                      { key: 'position', placeholder: 'Current Position' },
                      { key: 'company', placeholder: 'Company', required: true },
                      { key: 'location', placeholder: 'Location' },
                      { key: 'email', placeholder: 'Email' },
                      { key: 'linkedin', placeholder: 'LinkedIn URL' },
                    ].map(field => (
                      <input
                        key={field.key}
                        type="text"
                        value={manualEntry[field.key]}
                        onChange={e => onManualEntryChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                        className="bg-[#23242a] border border-[#374151] rounded-lg px-3 py-2 text-sm text-white placeholder-[#9ca3af] focus:outline-none focus:border-[#10b981]"
                      />
                    ))}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={addingManual}
                        className="flex-1 bg-[#10b981] text-white rounded-lg text-sm font-semibold hover:bg-[#059669] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {addingManual ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={onToggleManualEntry}
                        className="flex-1 bg-[#374151] text-[#e4e6eb] rounded-lg text-sm font-semibold hover:bg-[#4b5563] transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-center">
        <button
          onClick={onToggleManualEntry}
          className="px-6 py-2 rounded-lg bg-[#f5f5f0] text-gray-800 font-semibold hover:bg-[#e8e8e0] transition-colors"
        >
          {showManualEntry ? 'Cancel' : 'Add Manually'}
        </button>
      </div>
      {showCreateCategory && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={onOpenCreateCategory}
            className="px-4 py-2 bg-[#374151] text-[#e4e6eb] rounded-lg hover:bg-[#4b5563] transition-colors text-sm font-semibold"
          >
            Create Category
          </button>
        </div>
      )}
    </div>
  );
};

export { SavedAlumniTable };
